import React, { useState } from "react";
import {
  AlertTriangle,
  Ban,
  ChevronDown,
  Coins,
  Files,
  Info,
  Moon,
  Radio,
  X,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS } from "@/components/trade-filter-selects";
import { cn } from "@/lib/utils";
import type { StrategyRdBacktestWarning } from "../../../types/strategyRd";
import { strategyRdMetaClassName } from "./strategyRdCardShell";

/** Icons only — the texts come from the BE as they are. */
const ICONS: Record<string, LucideIcon> = {
  feed_approximate: Radio,
  costs_missing: Coins,
  margin_calls: AlertTriangle,
  margin_skipped: Ban,
  financing_not_included: Moon,
  two_scripts_sizing: Files,
};

const DISMISSED_KEY = "strategyRd.dismissedWarnings";

function loadDismissed(): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(codes: Set<string>): void {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(codes)));
  } catch {
    // Storage blocked — dismissal lasts until the page reloads.
  }
}

export type StrategyRdBacktestWarningsProps = {
  warnings: StrategyRdBacktestWarning[] | null | undefined;
  /** `check_robustness` action. Omitted → no button. */
  onCheckRobustness?: () => void;
  /** `open_settings` action with its `field`. Omitted → no button. */
  onOpenSetting?: (field: string) => void;
  /** One row of badges; a badge opens its message and action underneath. */
  compact?: boolean;
};

/**
 * What the user must know before trusting a backtest (§6). Amber banners for
 * `warning` (dismissible for the session), one grey line for `info`.
 */
export function StrategyRdBacktestWarnings({
  warnings,
  onCheckRobustness,
  onOpenSetting,
  compact = false,
}: StrategyRdBacktestWarningsProps): JSX.Element | null {
  const [dismissed, setDismissed] = useState<Set<string>>(loadDismissed);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const shown = (warnings ?? []).filter(
    (w) => !(w.severity === "warning" && dismissed.has(w.code))
  );
  if (shown.length === 0) return null;

  const dismiss = (code: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(code);
      saveDismissed(next);
      return next;
    });
  };

  const actionFor = (w: StrategyRdBacktestWarning): (() => void) | null => {
    const a = w.action;
    if (!a) return null;
    if (a.type === "check_robustness" && onCheckRobustness) return onCheckRobustness;
    if (a.type === "open_settings" && a.field && onOpenSetting) {
      const field = a.field;
      return () => onOpenSetting(field);
    }
    return null;
  };

  if (compact) {
    const open = shown.find((w) => w.code === openCode) ?? null;
    return (
      <div className="flex min-w-0 flex-col gap-2" data-strategy-rd-warnings="compact">
        <div className="flex flex-wrap items-center gap-1.5">
          {shown.map((w) => (
            <WarningBadge
              key={w.code}
              warning={w}
              open={w.code === openCode}
              onToggle={() => setOpenCode((c) => (c === w.code ? null : w.code))}
            />
          ))}
        </div>
        {open ? (
          open.severity === "warning" ? (
            <WarningBanner
              warning={open}
              onAction={actionFor(open)}
              onDismiss={() => {
                dismiss(open.code);
                setOpenCode(null);
              }}
            />
          ) : (
            <div
              data-strategy-rd-warning={open.code}
              className="flex flex-col items-start gap-1.5 !rounded-[6px] !border !border-solid !border-border bg-background px-4 py-3 dark:bg-sidebar"
            >
              <p className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">{open.title}</p>
              {open.message ? <p className={strategyRdMetaClassName}>{open.message}</p> : null}
              {actionFor(open) && open.action ? (
                <Button
                  type="button"
                  variant="gradient"
                  size="sm"
                  data-font="primary"
                  className="mt-1 h-8"
                  onClick={actionFor(open) ?? undefined}
                >
                  <span className="normal-case tracking-normal">{open.action.label}</span>
                </Button>
              ) : null}
            </div>
          )
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-strategy-rd-warnings="">
      {shown.map((w) =>
        w.severity === "warning" ? (
          <WarningBanner
            key={w.code}
            warning={w}
            onAction={actionFor(w)}
            onDismiss={() => dismiss(w.code)}
          />
        ) : (
          <InfoLine key={w.code} warning={w} onAction={actionFor(w)} />
        )
      )}
    </div>
  );
}

function WarningBadge({
  warning,
  open,
  onToggle,
}: {
  warning: StrategyRdBacktestWarning;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  const isWarning = warning.severity === "warning";
  const Icon = ICONS[warning.code] ?? (isWarning ? AlertTriangle : Info);
  return (
    // The app's dropdown trigger (Statistics "Views", Trade Log "Columns"): one control language.
    <button
      type="button"
      aria-expanded={open}
      data-state={open ? "open" : "closed"}
      onClick={onToggle}
      data-strategy-rd-warning-badge={warning.code}
      data-slot="select-trigger"
      data-size="default"
      data-trade-log-columns-trigger=""
      className={cn(
        TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS,
        // White on the results canvas (the trigger's own fill is the canvas grey).
        "max-w-[24rem] !bg-background hover:!bg-background data-[state=open]:!bg-background dark:!bg-sidebar"
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="truncate">{warning.title}</span>
      <ChevronDown
        className={cn("size-4 shrink-0 transition-transform", open ? "rotate-180" : "")}
        aria-hidden
      />
    </button>
  );
}

function WarningBanner({
  warning,
  onAction,
  onDismiss,
}: {
  warning: StrategyRdBacktestWarning;
  onAction: (() => void) | null;
  onDismiss: () => void;
}): JSX.Element {
  const Icon = ICONS[warning.code] ?? AlertTriangle;
  return (
    <div
      role="alert"
      data-strategy-rd-warning={warning.code}
      className="flex items-start gap-3 !rounded-[6px] !border !border-solid !border-border bg-background px-4 py-3 dark:bg-sidebar"
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">{warning.title}</p>
        {warning.message ? <p className={strategyRdMetaClassName}>{warning.message}</p> : null}
        {onAction && warning.action ? (
          <div>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="mt-1 h-8"
              onClick={onAction}
            >
              <span className="normal-case tracking-normal">{warning.action.label}</span>
            </Button>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="shrink-0 text-muted-foreground hover:text-foreground"
        aria-label={`Dismiss: ${warning.title}`}
        onClick={onDismiss}
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}

function InfoLine({
  warning,
  onAction,
}: {
  warning: StrategyRdBacktestWarning;
  onAction: (() => void) | null;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[warning.code] ?? Info;
  const expandable = Boolean(warning.message) || Boolean(onAction);
  return (
    <div data-strategy-rd-warning={warning.code} className="flex flex-col gap-1 px-1">
      <button
        type="button"
        disabled={!expandable}
        aria-expanded={expandable ? open : undefined}
        onClick={() => setOpen((v) => !v)}
        className={`${strategyRdMetaClassName} flex items-center gap-2 text-left enabled:hover:text-foreground`}
      >
        <Icon className="size-3.5 shrink-0" aria-hidden />
        <span>{warning.title}</span>
        {expandable ? (
          <ChevronDown
            className={`size-3.5 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
            aria-hidden
          />
        ) : null}
      </button>
      {open ? (
        <div className="flex flex-col items-start gap-1 pl-[22px]">
          {warning.message ? <p className={strategyRdMetaClassName}>{warning.message}</p> : null}
          {onAction && warning.action ? (
            <Button type="button" variant="gradient" size="sm" data-font="primary" className="h-8" onClick={onAction}>
              <span className="normal-case tracking-normal">{warning.action.label}</span>
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
